import { createElement, forwardRef } from 'react';
import {
  HugeiconsIcon,
  type HugeiconsIconProps,
  type IconSvgElement as HugeIconDefinition,
} from '@hugeicons/react';
import {
  Activity04Icon,
  AddCircleIcon,
  Airplane01Icon,
  Alert02Icon,
  AlertCircleIcon,
  ArrowDown01Icon,
  ArrowDown02Icon,
  ArrowLeft01Icon,
  ArrowLeft02Icon,
  ArrowRight01Icon,
  ArrowRight02Icon,
  ArrowUp01Icon,
  ArrowUp02Icon,
  ArrowUpDownIcon,
  ArrowUpRight02Icon,
  Award02Icon,
  BarChartIcon,
  BatteryFullIcon,
  BatteryMedium02Icon,
  BedDoubleIcon,
  BotIcon,
  BrainIcon,
  Building02Icon,
  BulbIcon,
  Calendar03Icon,
  CalendarAdd01Icon,
  CalendarCheckIn01Icon,
  Camera01Icon,
  Cancel01Icon,
  CancelCircleIcon,
  ChampionIcon,
  ChartColumnIcon,
  ChartDecreaseIcon,
  ChartIncreaseIcon,
  CheckListIcon,
  CheckmarkCircle02Icon,
  CircleIcon,
  Clock03Icon,
  CloudUploadIcon,
  Coffee01Icon,
  CookieIcon,
  CopyIcon,
  CrownIcon,
  DashboardSpeed01Icon,
  DatabaseIcon,
  DatabaseLightningIcon,
  Delete02Icon,
  Download01Icon,
  DropletIcon,
  DropletOffIcon,
  Dumbbell02Icon,
  EyeIcon,
  FavouriteIcon,
  FastWindIcon,
  File02Icon,
  Files01Icon,
  FireIcon,
  GiftIcon,
  GlobeIcon,
  HandGripIcon,
  HandHelpingIcon,
  HelpCircleIcon,
  Home05Icon,
  ImageNotFound01Icon,
  IceCubesIcon,
  InformationCircleIcon,
  JusticeScale01Icon,
  Key01Icon,
  KitchenUtensilsIcon,
  Leaf01Icon,
  Link01Icon,
  LinkSquare02Icon,
  LoaderPinwheelIcon,
  LockIcon,
  LockKeyIcon,
  LungsIcon,
  MagicWand02Icon,
  Mail01Icon,
  MailAdd01Icon,
  MailSend01Icon,
  MapPinIcon,
  MedicalMaskIcon,
  Medal01Icon,
  MessageCircleReplyIcon,
  Mic01Icon,
  Minimize02Icon,
  MinusSignIcon,
  MoonIcon,
  MoreHorizontalIcon,
  NextIcon,
  Notification01Icon,
  NotificationOff01Icon,
  PanelLeftIcon,
  PauseIcon,
  Pen02Icon,
  PencilEdit01Icon,
  PencilIcon,
  PinIcon,
  PinOffIcon,
  PlayIcon,
  PlusSignIcon,
  Pulse01Icon,
  Refresh01Icon,
  RemoveCircleIcon,
  RotateLeft01Icon,
  RunningShoesIcon,
  SaveIcon,
  Scroll01Icon,
  Search01Icon,
  SecurityCheckIcon,
  SecurityWarningIcon,
  ServerStack01Icon,
  Settings01Icon,
  Share01Icon,
  Shield01Icon,
  SlidersHorizontalIcon,
  SmartPhone01Icon,
  SmartWatch01Icon,
  SparklesIcon,
  SquareIcon,
  StarIcon,
  StopCircleIcon,
  Sun01Icon,
  SunsetIcon,
  Target01Icon,
  TelephoneIcon,
  ThermometerIcon,
  Tick02Icon,
  Ticket01Icon,
  Timer01Icon,
  TransactionHistoryIcon,
  Unlink01Icon,
  Upload01Icon,
  UserCircleIcon,
  UserGroupIcon,
  UserIcon,
  UserSettings01Icon,
  ViewOffIcon,
  WaveIcon,
  WheatIcon,
  ZapIcon,
} from '@hugeicons/core-free-icons';

import { cn } from '@/lib/utils';

export type HugeIconProps = Omit<HugeiconsIconProps, 'icon' | 'altIcon'>;
export type HugeIconComponent = ReturnType<typeof createHugeIcon>;

function createHugeIcon(icon: HugeIconDefinition, displayName: string) {
  const Component = forwardRef<SVGSVGElement, HugeIconProps>(
    ({ color = 'currentColor', size = 24, strokeWidth = 1.9, ...props }, ref) =>
      createElement(HugeiconsIcon, {
        ref,
        color,
        focusable: 'false',
        icon,
        size,
        strokeWidth,
        ...props,
      }),
  );

  Component.displayName = displayName;
  return Component;
}

export const Activity = createHugeIcon(Activity04Icon, 'Activity');
export const Airplane = createHugeIcon(Airplane01Icon, 'Airplane');
export const AlertCircle = createHugeIcon(AlertCircleIcon, 'AlertCircle');
export const AlertTriangle = createHugeIcon(Alert02Icon, 'AlertTriangle');
export const ArrowDown = createHugeIcon(ArrowDown02Icon, 'ArrowDown');
export const ArrowLeft = createHugeIcon(ArrowLeft02Icon, 'ArrowLeft');
export const ArrowRight = createHugeIcon(ArrowRight02Icon, 'ArrowRight');
export const ArrowUp = createHugeIcon(ArrowUp02Icon, 'ArrowUp');
export const ArrowUpDown = createHugeIcon(ArrowUpDownIcon, 'ArrowUpDown');
export const ArrowUpRight = createHugeIcon(ArrowUpRight02Icon, 'ArrowUpRight');
export const Award = createHugeIcon(Award02Icon, 'Award');
export const BarChart = createHugeIcon(BarChartIcon, 'BarChart');
export const BarChart3 = createHugeIcon(ChartColumnIcon, 'BarChart3');
export const Battery = createHugeIcon(BatteryFullIcon, 'Battery');
export const BatteryMedium = createHugeIcon(BatteryMedium02Icon, 'BatteryMedium');
export const BedDouble = createHugeIcon(BedDoubleIcon, 'BedDouble');
export const Bell = createHugeIcon(Notification01Icon, 'Bell');
export const BellOff = createHugeIcon(NotificationOff01Icon, 'BellOff');
export const Bot = createHugeIcon(BotIcon, 'Bot');
export const Brain = createHugeIcon(BrainIcon, 'Brain');
export const Building2 = createHugeIcon(Building02Icon, 'Building2');
export const Calendar = createHugeIcon(Calendar03Icon, 'Calendar');
export const CalendarCheck = createHugeIcon(CalendarCheckIn01Icon, 'CalendarCheck');
export const CalendarClock = createHugeIcon(Clock03Icon, 'CalendarClock');
export const CalendarDays = createHugeIcon(Calendar03Icon, 'CalendarDays');
export const CalendarIcon = Calendar;
export const CalendarPlus2 = createHugeIcon(CalendarAdd01Icon, 'CalendarPlus2');
export const Camera = createHugeIcon(Camera01Icon, 'Camera');
export const Check = createHugeIcon(Tick02Icon, 'Check');
export const CheckCircle = createHugeIcon(CheckmarkCircle02Icon, 'CheckCircle');
export const CheckCircle2 = CheckCircle;
export const ChevronDown = createHugeIcon(ArrowDown01Icon, 'ChevronDown');
export const ChevronLeft = createHugeIcon(ArrowLeft01Icon, 'ChevronLeft');
export const ChevronRight = createHugeIcon(ArrowRight01Icon, 'ChevronRight');
export const ChevronUp = createHugeIcon(ArrowUp01Icon, 'ChevronUp');
export const Circle = createHugeIcon(CircleIcon, 'Circle');
export const CircleCheck = CheckCircle;
export const ClipboardList = createHugeIcon(CheckListIcon, 'ClipboardList');
export const Clock = createHugeIcon(Clock03Icon, 'Clock');
export const Clock3 = Clock;
export const CloudUpload = createHugeIcon(CloudUploadIcon, 'CloudUpload');
export const Coffee = createHugeIcon(Coffee01Icon, 'Coffee');
export const Cookie = createHugeIcon(CookieIcon, 'Cookie');
export const Copy = createHugeIcon(CopyIcon, 'Copy');
export const Crown = createHugeIcon(CrownIcon, 'Crown');
export const Database = createHugeIcon(DatabaseIcon, 'Database');
export const DatabaseZap = createHugeIcon(DatabaseLightningIcon, 'DatabaseZap');
export const Dot = createHugeIcon(CircleIcon, 'Dot');
export const Download = createHugeIcon(Download01Icon, 'Download');
export const Droplet = createHugeIcon(DropletIcon, 'Droplet');
export const Droplets = createHugeIcon(DropletOffIcon, 'Droplets');
export const Dumbbell = createHugeIcon(Dumbbell02Icon, 'Dumbbell');
export const ExternalLink = createHugeIcon(LinkSquare02Icon, 'ExternalLink');
export const Eye = createHugeIcon(EyeIcon, 'Eye');
export const EyeOff = createHugeIcon(ViewOffIcon, 'EyeOff');
export const FileText = createHugeIcon(File02Icon, 'FileText');
export const Files = createHugeIcon(Files01Icon, 'Files');
export const Flame = createHugeIcon(FireIcon, 'Flame');
export const FlaskConical = createHugeIcon(ThermometerIcon, 'FlaskConical');
export const Footprints = createHugeIcon(RunningShoesIcon, 'Footprints');
export const Gauge = createHugeIcon(DashboardSpeed01Icon, 'Gauge');
export const Gift = createHugeIcon(GiftIcon, 'Gift');
export const Globe = createHugeIcon(GlobeIcon, 'Globe');
export const GripVertical = createHugeIcon(HandGripIcon, 'GripVertical');
export const Heart = createHugeIcon(FavouriteIcon, 'Heart');
export const HeartHandshake = createHugeIcon(HandHelpingIcon, 'HeartHandshake');
export const HeartPulse = createHugeIcon(Pulse01Icon, 'HeartPulse');
export const HelpCircle = createHugeIcon(HelpCircleIcon, 'HelpCircle');
export const History = createHugeIcon(TransactionHistoryIcon, 'History');
export const Home = createHugeIcon(Home05Icon, 'Home');
export const ImageOff = createHugeIcon(ImageNotFound01Icon, 'ImageOff');
export const IceCubes = createHugeIcon(IceCubesIcon, 'IceCubes');
export const Info = createHugeIcon(InformationCircleIcon, 'Info');
export const Key = createHugeIcon(Key01Icon, 'Key');
export const KeyRound = Key;
export const Leaf = createHugeIcon(Leaf01Icon, 'Leaf');
export const Lightbulb = createHugeIcon(BulbIcon, 'Lightbulb');
export const Link2 = createHugeIcon(Link01Icon, 'Link2');
export const Loader2 = createHugeIcon(LoaderPinwheelIcon, 'Loader2');
export const Lock = createHugeIcon(LockIcon, 'Lock');
export const LockKeyhole = createHugeIcon(LockKeyIcon, 'LockKeyhole');
export const Lungs = createHugeIcon(LungsIcon, 'Lungs');
export const Mail = createHugeIcon(Mail01Icon, 'Mail');
export const MailPlus = createHugeIcon(MailAdd01Icon, 'MailPlus');
export const MapPin = createHugeIcon(MapPinIcon, 'MapPin');
export const MedicalMask = createHugeIcon(MedicalMaskIcon, 'MedicalMask');
export const Medal = createHugeIcon(Medal01Icon, 'Medal');
export const MessageCircle = createHugeIcon(MessageCircleReplyIcon, 'MessageCircle');
export const Mic = createHugeIcon(Mic01Icon, 'Mic');
export const Minimize2 = createHugeIcon(Minimize02Icon, 'Minimize2');
export const Minus = createHugeIcon(MinusSignIcon, 'Minus');
export const MinusCircle = createHugeIcon(RemoveCircleIcon, 'MinusCircle');
export const Moon = createHugeIcon(MoonIcon, 'Moon');
export const MoreHorizontal = createHugeIcon(MoreHorizontalIcon, 'MoreHorizontal');
export const PanelLeft = createHugeIcon(PanelLeftIcon, 'PanelLeft');
export const Pause = createHugeIcon(PauseIcon, 'Pause');
export const PenLine = createHugeIcon(Pen02Icon, 'PenLine');
export const Pencil = createHugeIcon(PencilIcon, 'Pencil');
export const PencilLine = createHugeIcon(PencilEdit01Icon, 'PencilLine');
export const Phone = createHugeIcon(TelephoneIcon, 'Phone');
export const Pin = createHugeIcon(PinIcon, 'Pin');
export const PinOff = createHugeIcon(PinOffIcon, 'PinOff');
export const Play = createHugeIcon(PlayIcon, 'Play');
export const Plus = createHugeIcon(PlusSignIcon, 'Plus');
export const PlusCircle = createHugeIcon(AddCircleIcon, 'PlusCircle');
export const RefreshCw = createHugeIcon(Refresh01Icon, 'RefreshCw');
export const RotateCcw = createHugeIcon(RotateLeft01Icon, 'RotateCcw');
export const Save = createHugeIcon(SaveIcon, 'Save');
export const Scale = createHugeIcon(JusticeScale01Icon, 'Scale');
export const ScrollText = createHugeIcon(Scroll01Icon, 'ScrollText');
export const Search = createHugeIcon(Search01Icon, 'Search');
export const Send = createHugeIcon(MailSend01Icon, 'Send');
export const Server = createHugeIcon(ServerStack01Icon, 'Server');
export const Settings = createHugeIcon(Settings01Icon, 'Settings');
export const Share2 = createHugeIcon(Share01Icon, 'Share2');
export const Shield = createHugeIcon(Shield01Icon, 'Shield');
export const ShieldAlert = createHugeIcon(SecurityWarningIcon, 'ShieldAlert');
export const ShieldCheck = createHugeIcon(SecurityCheckIcon, 'ShieldCheck');
export const SkipForward = createHugeIcon(NextIcon, 'SkipForward');
export const SlidersHorizontal = createHugeIcon(SlidersHorizontalIcon, 'SlidersHorizontal');
export const Smartphone = createHugeIcon(SmartPhone01Icon, 'Smartphone');
export const Sparkles = createHugeIcon(SparklesIcon, 'Sparkles');
export const Square = createHugeIcon(SquareIcon, 'Square');
export const Star = createHugeIcon(StarIcon, 'Star');
export const StopCircle = createHugeIcon(StopCircleIcon, 'StopCircle');
export const Sun = createHugeIcon(Sun01Icon, 'Sun');
export const Sunset = createHugeIcon(SunsetIcon, 'Sunset');
export const Target = createHugeIcon(Target01Icon, 'Target');
export const Thermometer = createHugeIcon(ThermometerIcon, 'Thermometer');
export const Ticket = createHugeIcon(Ticket01Icon, 'Ticket');
export const Timer = createHugeIcon(Timer01Icon, 'Timer');
export const Trash2 = createHugeIcon(Delete02Icon, 'Trash2');
export const TrendingDown = createHugeIcon(ChartDecreaseIcon, 'TrendingDown');
export const TrendingUp = createHugeIcon(ChartIncreaseIcon, 'TrendingUp');
export const Trophy = createHugeIcon(ChampionIcon, 'Trophy');
export const Unlink = createHugeIcon(Unlink01Icon, 'Unlink');
export const Upload = createHugeIcon(Upload01Icon, 'Upload');
export const User = createHugeIcon(UserIcon, 'User');
export const UserCircle = createHugeIcon(UserCircleIcon, 'UserCircle');
export const UserCog = createHugeIcon(UserSettings01Icon, 'UserCog');
export const Users = createHugeIcon(UserGroupIcon, 'Users');
export const UtensilsCrossed = createHugeIcon(KitchenUtensilsIcon, 'UtensilsCrossed');
export const WandSparkles = createHugeIcon(MagicWand02Icon, 'WandSparkles');
export const Watch = createHugeIcon(SmartWatch01Icon, 'Watch');
export const Waves = createHugeIcon(WaveIcon, 'Waves');
export const Wheat = createHugeIcon(WheatIcon, 'Wheat');
export const Wind = createHugeIcon(FastWindIcon, 'Wind');
export const X = createHugeIcon(Cancel01Icon, 'X');
export const XCircle = createHugeIcon(CancelCircleIcon, 'XCircle');
export const Zap = createHugeIcon(ZapIcon, 'Zap');

type AppIconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type AppIconTone = 'default' | 'muted' | 'primary' | 'success' | 'warning' | 'danger';
type AppIconStroke = 'fine' | 'default' | 'strong';
type AppIconSlot = 'inline-start' | 'inline-end' | 'standalone' | 'only';

const SIZE_CLASS: Record<AppIconSize, string> = {
  xs: 'size-3.5',
  sm: 'size-4',
  md: 'size-[1.125rem]',
  lg: 'size-5',
  xl: 'size-6',
};

const SIZE_PX: Record<AppIconSize, number> = {
  xs: 14,
  sm: 16,
  md: 18,
  lg: 20,
  xl: 24,
};

const TONE_CLASS: Record<AppIconTone, string> = {
  default: 'text-current',
  muted: 'text-muted-foreground',
  primary: 'text-primary',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-destructive',
};

const STROKE_WIDTH: Record<AppIconStroke, number> = {
  fine: 1.75,
  default: 1.9,
  strong: 2.15,
};

interface AppIconClassNameOptions {
  size?: AppIconSize;
  tone?: AppIconTone;
  className?: string;
}

function appIconClassName({
  size = 'sm',
  tone = 'default',
  className,
}: AppIconClassNameOptions = {}) {
  return cn('shrink-0', SIZE_CLASS[size], TONE_CLASS[tone], className);
}

interface AppIconProps extends Omit<HugeIconProps, 'size' | 'strokeWidth' | 'color'> {
  icon: HugeIconComponent;
  size?: AppIconSize;
  tone?: AppIconTone;
  stroke?: AppIconStroke;
  slot?: AppIconSlot;
  decorative?: boolean;
}

export function AppIcon({
  icon: Icon,
  size = 'sm',
  tone = 'default',
  stroke = 'default',
  slot = 'standalone',
  decorative = true,
  className,
  ...props
}: AppIconProps) {
  return (
    <Icon
      aria-hidden={decorative}
      className={appIconClassName({ size, tone, className })}
      data-icon={slot}
      focusable="false"
      strokeWidth={STROKE_WIDTH[stroke]}
      {...props}
    />
  );
}

interface AppHugeIconProps extends Omit<
  HugeiconsIconProps,
  'icon' | 'size' | 'strokeWidth' | 'color'
> {
  icon: HugeIconDefinition;
  size?: AppIconSize;
  tone?: AppIconTone;
  stroke?: AppIconStroke;
  slot?: AppIconSlot;
  decorative?: boolean;
}

export function AppHugeIcon({
  icon,
  size = 'sm',
  tone = 'default',
  stroke = 'fine',
  slot = 'standalone',
  decorative = true,
  className,
  ...props
}: AppHugeIconProps) {
  return (
    <HugeiconsIcon
      aria-hidden={decorative}
      className={appIconClassName({ size, tone, className })}
      color="currentColor"
      data-icon={slot}
      focusable="false"
      icon={icon}
      role={decorative ? undefined : 'img'}
      size={SIZE_PX[size]}
      strokeWidth={STROKE_WIDTH[stroke]}
      {...props}
    />
  );
}

export type { HugeIconDefinition };
